import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useFeedbackModalStore } from 'store/feedbackModalStore'
import { shallow } from 'zustand/shallow'

import { Box, Flex, Link as DDSLink, SimpleGrid, styled } from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import { Metadata } from 'components/Metadata'
import OnboardingShepherd from 'components/OnboardingShepherd'
import ScoringInProgressBanner from 'components/PreferencesModal/AccountFitPreferences/ScoringInProgressBanner'
import { driftLoadingStateBuilder } from 'components/States'
import { CurrentUserContext } from 'context/currentUserContext'
import { ICPSettingsContext } from 'context/icpSettingsContext'
import useEducationState from 'hooks/useEducationState'
import useNotifications from 'hooks/useNotifications'
import useSavedFilters from 'hooks/useSavedFilters'
import CCRMFeedbackModal from 'sections/CCRM/FeedbackModal'
import { segmentTrack, TrackType } from 'utils/tracking'

import AccountEngagementOverview from './AccountEngagementOverview'
import { AccountOpportunityContextProvider } from './AccountOpportunityContext'
import DashboardFilters from './DashboardFilters'
import DashboardHeader from './DashboardHeader'
import RecentActivity from './RecentActivity'

export const PrimaryRouterLink = ({
  children,
  to,
  css,
  onClick,
  ...props
}: {
  children: any
  to: any
  onClick?: () => void
  css?: { [key: string]: string }
}) => (
  <DDSLink
    variant="inline"
    to={to}
    as={Link}
    onClick={() => onClick && onClick()}
    {...props}
    css={{
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...css
    }}>
    {children}
  </DDSLink>
)

const Container = styled(Box, {
  backgroundColor: '$gray1',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowY: 'auto',
  gap: '0'
})

const ContentContainer = styled(Box, {
  padding: '0 $4 $2',
  height: '100%',
  width: '100%',
  margin: '0 auto'
})

const ActivityDashboard = () => {
  const [accountDataSize, setAccountDataSize] = useState<number | undefined>(undefined)
  const [resultSize, setResultSize] = useState<number | undefined>(undefined)
  const [filterSelectOpen, setFilterSelectOpen] = useState<boolean | undefined>(undefined)
  const { isCheckListOpen, hasFinishedOnboarding, setFinishedOnboarding } = useEducationState()

  const shouldShowOnboarding = !access.isAdmin() && !hasFinishedOnboarding && !isCheckListOpen
  const [showOnboarding] = useState<boolean>(shouldShowOnboarding)
  const { info } = useNotifications()

  const { scoringInProgress } = useContext(ICPSettingsContext)
  const [showScoringInProgress, setShowScoringInProgress] = useState<boolean>(scoringInProgress)

  const currentUser = useContext(CurrentUserContext)

  const { showModal, toggleModal } = useFeedbackModalStore(
    state => ({
      showModal: state.showModal,
      toggleModal: state.toggleModal
    }),
    shallow
  )

  const {
    activeSavedFilter,
    updateActiveFilterId,
    sharedFilters,
    privateFilters,
    filtersLoaded
  } = useSavedFilters()
  const hasLiveViewFromReach = access.hasGates('has_live_view_with_reach_events')
  const LoadingComponent = React.useMemo(
    () => driftLoadingStateBuilder('Loading your dashboard'),
    []
  )

  useEffect(() => {
    segmentTrack(TrackType.DASHBOARD_PAGE_ACCESSED)
  }, [])

  if (!filtersLoaded) {
    return <LoadingComponent />
  }

  return (
    <AccountOpportunityContextProvider>
      <Container>
        {showScoringInProgress && (
          <ScoringInProgressBanner onClose={() => setShowScoringInProgress(false)} />
        )}
        {showOnboarding && (
          <OnboardingShepherd
            id="filter-tour"
            runOnMount
            hidden={!showOnboarding}
            ctaText="Learn how to use Prospector"
            startEventName={TrackType.DASHBOARD_ONBOARDING_SHEPHERD_TOUR_STARTED}
            dismissedEventName={TrackType.DASHBOARD_ONBOARDING_SHEPHERD_TOUR_DISMISSED}
            onDismiss={setFinishedOnboarding}
            steps={[
              {
                title: 'Prioritize engaged accounts',
                text:
                  'Let Drift’s AI-powered engagement score surface highly engaged accounts for you.',
                cssSelector: '.ai-engagement-onboarding:first-child',
                trackEvent: TrackType.DASHBOARD_ONBOARDING_SHEPHERD_AI_ENGAGEMENT_STEP_VIEWED
              },
              {
                title: 'Focus on accounts you care about ',
                text:
                  'Filter your dashboard by all accounts you own or create a saved filter to reduce noise.',
                cssSelector: '#react-select-2-listbox',
                position: 'left',
                beforeShowPromise: () =>
                  new Promise(resolve => {
                    setTimeout(() => {
                      setFilterSelectOpen(true)
                      resolve()
                    }, 200)
                  }),
                onHide: () => setFilterSelectOpen(false),
                trackEvent: TrackType.DASHBOARD_ONBOARDING_SHEPHERD_SAVED_FILTERS_STEP_VIEWED
              },
              {
                title: 'Get notified about engaged accounts',
                text:
                  'Enable notifications so you never miss activity from accounts you care about.',
                cssSelector: '.notifications-onboarding',
                primaryButtonText: 'Done',
                onShow: () => {
                  // since we run on mount, track this as a completed event
                  segmentTrack(TrackType.DASHBOARD_ONBOARDING_SHEPHERD_TOUR_COMPLETED, {
                    userId: currentUser?.id,
                    orgId: currentUser?.orgId
                  })
                  setFinishedOnboarding()
                },
                trackEvent: TrackType.DASHBOARD_ONBOARDING_SHEPHERD_NOTIFICATIONS_STEP_VIEWED,
                secondaryButtonText: 'Continue learning',
                secondaryButtonCustomAction: () => {
                  segmentTrack(TrackType.DASHBOARD_ONBOARDING_SHEPHERD_LEARN_MORE_BUTTON_CLICKED, {
                    userId: currentUser?.id,
                    orgId: currentUser?.orgId
                  })
                  window.open(
                    'https://help.salesloft.com/s/article/Drift-Prospector-Quick-Start-Sales-Guide',
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              }
            ]}
          />
        )}
        <Metadata title="Drift | Prospector dashboard" />
        <DashboardHeader hasLiveViewFromReach={hasLiveViewFromReach} />
        <ContentContainer>
          <SimpleGrid
            css={{
              rowGap: '$2'
            }}
            columns={8}
            gap={3}
            fullWidth>
            <Box
              css={{
                gridColumn: 'span 8'
              }}>
              <DashboardFilters
                filterSelectOpen={filterSelectOpen}
                resultSize={resultSize}
                accountDataSize={accountDataSize}
                activeSavedFilter={activeSavedFilter}
                updateActiveFilterId={updateActiveFilterId}
                sharedFilters={sharedFilters}
                privateFilters={privateFilters}
              />
            </Box>
            <AccountEngagementOverview
              setDataSize={setAccountDataSize}
              setResultSize={setResultSize}
            />
            <Flex
              className="recent-activity"
              direction="column"
              gap={2}
              css={{
                gridColumn: 'span 2',
                maxWidth: '420px',
                justifySelf: 'flex-end'
              }}
              fullWidth>
              <RecentActivity />
            </Flex>
          </SimpleGrid>
        </ContentContainer>
        {showModal && (
          <CCRMFeedbackModal
            onClose={() => {
              toggleModal(false)
            }}
            onSubmit={() => {
              info('Thanks for taking the time to make Drift better!')
            }}
          />
        )}
      </Container>
    </AccountOpportunityContextProvider>
  )
}

export default ActivityDashboard
